import * as m from '@staiter/i18n';
import type { ExcelSnapshot, OverrideDiff } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// S7.1–7.2 — Excel snapshots browser + manual-override upload/diff dialog.
import { createFileRoute } from '@tanstack/react-router';
import { Download, Send, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { ListPageShell, PageHeader, Table } from '~/components/data';
import { OverrideDiffViewer } from '~/components/excel/OverrideDiffViewer';
import { useToast } from '~/components/feedback';
import { Button, ConfirmDialog, DateInput, Dialog } from '~/components/primitives';
import {
  fetchExcelSnapshots,
  fetchOverrideDiff,
  getExcelDownloadUrl,
  publishExcelSnapshot,
  uploadExcelOverride,
} from '~/lib/api/excel';
import { useJobPolling } from '~/lib/api/jobs';
import { useListPageState } from '~/lib/hooks/useListPageState';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/excel')({
  staticData: { minMainWidth: 960 },
  component: ExcelBrowser,
});

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function ExcelBrowser(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [publishJobId, setPublishJobId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [overrideDiff, setOverrideDiff] = useState<OverrideDiff | null>(null);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const snapsQ = useQuery({
    queryKey: ['excel-snapshots', period],
    queryFn: () => fetchExcelSnapshots(period),
    enabled: /^\d{4}-\d{2}$/.test(period),
  });
  const snapsState = useListPageState(snapsQ);

  const jobPoll = useJobPolling(publishJobId, Boolean(publishJobId));

  // Re-fetch override diff live via react-query (SSE handled in viewer).
  const overrideQ = useQuery({
    queryKey: ['excel-override', overrideDiff?.id],
    queryFn: () => (overrideDiff?.id ? fetchOverrideDiff(overrideDiff.id) : Promise.resolve(null)),
    enabled: Boolean(overrideDiff),
    initialData: () => overrideDiff ?? undefined,
  });

  const publishMut = useMutation({
    mutationFn: () => publishExcelSnapshot(period),
    onSuccess: (res) => {
      setPublishJobId(res.job_id);
      toast.push({
        variant: 'success',
        title: m.route_excel_toast_publish_started(),
        description: res.job_id,
      });
      void qc.invalidateQueries({ queryKey: ['excel-snapshots', period] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_excel_toast_publish_failed(),
        description: err.message,
      }),
  });

  const uploadMut = useMutation({
    mutationFn: ({ snapshotId, file }: { snapshotId: string; file: File }) =>
      uploadExcelOverride(snapshotId, file),
    onSuccess: (diff) => {
      setOverrideDiff(diff);
      setUploadOpen(false);
      setPendingFile(null);
      toast.push({
        variant: 'success',
        title: m.route_excel_toast_analyzed(),
        description: m.route_excel_toast_analyzed_desc({ count: diff.summary.total_changes ?? 0 }),
      });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_excel_toast_upload_failed(),
        description: err.message,
      }),
  });

  async function handleDownloadLatest(): Promise<void> {
    try {
      const url = await getExcelDownloadUrl(period);
      window.location.href = url;
    } catch (err) {
      toast.push({
        variant: 'error',
        title: m.route_excel_toast_download_failed(),
        description: (err as Error).message,
      });
    }
  }

  function confirmPublish(): void {
    setPublishConfirmOpen(true);
  }

  function openUploadForSnapshot(snapshotId: string): void {
    setSelectedSnapshot(snapshotId);
    setUploadOpen(true);
    setPendingFile(null);
  }

  const diffToShow = overrideQ.data ?? overrideDiff;

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_excel()}
        description={m.route_excel_description()}
        actions={
          <div className={shared.actions}>
            <Button
              variant="secondary"
              leadingIcon={<Download size={14} />}
              onClick={() => void handleDownloadLatest()}
            >
              {m.route_excel_btn_download_latest()}
            </Button>
            <Button
              variant="primary"
              leadingIcon={<Send size={14} />}
              onClick={confirmPublish}
              loading={publishMut.isPending}
            >
              {m.route_excel_btn_publish()}
            </Button>
          </div>
        }
      />

      <div className={shared.filters}>
        <DateInput
          label={m.route_excel_filter_period()}
          mode="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
        {publishJobId && jobPoll.data && (
          <div aria-live="polite">
            <span className={shared.subtle}>
              {m.route_excel_job_status({
                id: publishJobId.slice(0, 8),
                status: jobPoll.data.status,
                progress: jobPoll.data.progress,
              })}
            </span>
          </div>
        )}
      </div>

      <ListPageShell
        state={snapsState}
        emptyTitle={m.route_excel_empty_title()}
        emptyDescription={m.route_excel_empty_description()}
      >
        {(rows) => (
          <Table<ExcelSnapshot>
            ariaLabel={m.route_excel_aria()}
            data={rows}
            rowKey={(r) => r.id}
            columns={[
              {
                id: 'sha',
                header: m.route_excel_col_sha(),
                accessor: (r) => r.sha256.slice(0, 10),
                mono: true,
              },
              {
                id: 'created_at',
                header: m.route_excel_col_created(),
                accessor: (r) => new Date(r.created_at).toLocaleString(),
              },
              {
                id: 'by',
                header: m.route_excel_col_by(),
                accessor: (r) => r.created_by,
              },
              {
                id: 'size',
                header: m.route_excel_col_size(),
                accessor: (r) => m.route_excel_size_kb({ value: (r.size_bytes / 1024).toFixed(1) }),
                mono: true,
                align: 'right',
              },
              {
                id: 'schema',
                header: m.route_excel_col_schema(),
                accessor: (r) => r.schema_version,
                mono: true,
              },
              {
                id: 'actions',
                header: m.route_excel_col_actions(),
                accessor: (r) => (
                  <div className={shared.rowWrap}>
                    {r.download_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (r.download_url) window.location.href = r.download_url;
                        }}
                        leadingIcon={<Download size={12} />}
                      >
                        {m.route_excel_btn_download()}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openUploadForSnapshot(r.id)}
                      leadingIcon={<Upload size={12} />}
                    >
                      {m.route_excel_btn_upload_edits()}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>

      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title={m.route_excel_upload_title()}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setUploadOpen(false)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              variant="primary"
              disabled={!pendingFile || !selectedSnapshot}
              loading={uploadMut.isPending}
              onClick={() => {
                if (pendingFile && selectedSnapshot) {
                  uploadMut.mutate({ snapshotId: selectedSnapshot, file: pendingFile });
                }
              }}
            >
              {m.route_excel_upload_analyze()}
            </Button>
          </>
        }
      >
        <p className={shared.subtle}>
          {m.route_excel_upload_hint({ id: selectedSnapshot?.slice(0, 8) ?? '—' })}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setPendingFile(f);
          }}
          aria-label={m.route_excel_file_input_aria()}
        />
        {pendingFile && (
          <p className={shared.subtle}>
            {m.route_excel_file_size_row({
              name: pendingFile.name,
              size: (pendingFile.size / 1024).toFixed(1),
            })}
          </p>
        )}
      </Dialog>

      {diffToShow && (
        <section className={shared.sectionGap}>
          <h2 className={shared.sectionHeading}>{m.route_excel_section_review()}</h2>
          <OverrideDiffViewer diff={diffToShow} onChange={(next) => setOverrideDiff(next)} />
        </section>
      )}

      <ConfirmDialog
        open={publishConfirmOpen}
        onCancel={() => setPublishConfirmOpen(false)}
        onConfirm={() => {
          publishMut.mutate();
          setPublishConfirmOpen(false);
        }}
        title={m.route_excel_publish_confirm_title()}
        description={m.route_excel_publish_confirm_desc({ period })}
        confirmLabel={m.route_excel_publish_confirm_btn()}
        loading={publishMut.isPending}
      />
    </div>
  );
}
